import { useLingui } from '@lingui/react/macro';

import { formatInstant } from '@/shared/format/date';
import { DescriptionList } from './description-list';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';

interface ApprovalActor {
  readonly userIdDisplay?: string | null | undefined;
  readonly tenantDisplay?: string | null | undefined;
  readonly approvalTypeDisplay?: string | null | undefined;
}

// O drawer "Detalhes da aprovação" (`BankAccountItemDetails.tsx:22-111`), o mesmo do favorecido e da TED:
// criado e atualizado por, o fluxo de aprovação e o erro de processamento da própria linha, sem GET.

export interface ApprovalDetails {
  readonly createdBy?: ApprovalActor | null | undefined;
  readonly createdAt?: string | null | undefined;
  readonly updatedBy?: ApprovalActor | null | undefined;
  readonly updatedAt?: string | null | undefined;
  readonly approvals?:
    | readonly {
        readonly actionDisplay?: string | null | undefined;
        readonly user?: ApprovalActor | null | undefined;
        readonly createdAt?: string | null | undefined;
      }[]
    | null
    | undefined;
  readonly errorMessage?: string | null | undefined;
}

/** `formatWitTenant` (`StringFormats.ts:143-150`): o correspondente entre parênteses só no tenant raiz. */
function actorText(
  actor: ApprovalActor | null | undefined,
  root: boolean,
  notAvailable: string,
): string {
  if (!actor) return notAvailable;
  const name = actor.userIdDisplay ?? actor.approvalTypeDisplay ?? notAvailable;
  return root
    ? `${name} (${actor.tenantDisplay ?? notAvailable})`
    : (actor.userIdDisplay ?? notAvailable);
}

export function ApprovalDetailsSheet({
  details,
  description,
  root,
  onClose,
}: {
  readonly details: ApprovalDetails | null;
  readonly description: string;
  readonly root: boolean;
  readonly onClose: () => void;
}) {
  const { i18n, t } = useLingui();
  const notAvailable = t`N/D`;
  const date = (value: string | null | undefined) =>
    value ? (formatInstant(value, { locale: i18n.locale }) ?? value) : notAvailable;
  return (
    <Sheet open={details !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent size="sm">
        <SheetHeader>
          <SheetTitle>{t`Detalhes da aprovação`}</SheetTitle>
          <SheetDescription>{description}</SheetDescription>
        </SheetHeader>
        <SheetBody>
          {details ? (
            <div className="flex flex-col gap-4">
              {/* `BankAccountItemDetails.tsx:32-55`: atualização só quando houver `updatedBy`. */}
              <DescriptionList
                columns={1}
                items={[
                  {
                    id: 'createdBy',
                    label: t`Criado por`,
                    value: actorText(details.createdBy, root, notAvailable),
                  },
                  { id: 'createdAt', label: t`Data da criação`, value: date(details.createdAt) },
                  ...(details.updatedBy
                    ? [
                        {
                          id: 'updatedBy',
                          label: t`Atualizado por`,
                          value: actorText(details.updatedBy, root, notAvailable),
                        },
                        {
                          id: 'updatedAt',
                          label: t`Data da atualização`,
                          value: date(details.updatedAt),
                        },
                      ]
                    : []),
                ]}
              />
              {(details.approvals ?? []).length > 0 ? (
                <section className="flex flex-col gap-2">
                  <h3 className="text-base font-semibold">{t`Fluxo de aprovação`}</h3>
                  {(details.approvals ?? []).map((approval, index) => (
                    <DescriptionList
                      key={`${approval.createdAt ?? ''}-${String(index)}`}
                      columns={1}
                      items={[
                        {
                          id: 'answer',
                          label: t`Resposta`,
                          value: approval.actionDisplay === 'Aprovar' ? t`Aprovado` : t`Reprovado`,
                        },
                        {
                          id: 'operator',
                          label: t`Operador`,
                          value: actorText(approval.user, root, notAvailable),
                        },
                        {
                          id: 'answeredAt',
                          label: t`Data da resposta`,
                          value: date(approval.createdAt),
                        },
                      ]}
                    />
                  ))}
                </section>
              ) : null}
              {details.errorMessage ? (
                <section className="flex flex-col gap-2">
                  <h3 className="text-base font-semibold">{t`Erros de processamento`}</h3>
                  <p className="text-sm">{details.errorMessage}</p>
                </section>
              ) : null}
            </div>
          ) : null}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}
